// Thin wrapper over the Web Audio API. Owns the AudioContext, master + SFX +
// music gain nodes, and the in-memory buffer cache. Volumes are stored in
// localStorage so the player's pick survives reloads. The Settings panel reads
// and writes through `setMasterVolume / setSfxVolume / setMusicVolume`.
//
// Browsers gate AudioContext.start() behind a user gesture. ensureRunning() is
// called on first user interaction (the menu's Play button) so playback works
// without an "audio blocked" indicator.
//
// File-resolution: production audio ships as `.mp3` (the assets delivered for
// V1). The 16 placeholder cues that ship with the repo are `.wav` so a fresh
// checkout still has audible feedback without the production drop. `loadBuffer`
// tries `.mp3` first and falls back to `.wav` so both shapes work side-by-side
// without any per-cue config.
const STORAGE_KEY_MASTER = 'signalclash.audio.master';
const STORAGE_KEY_SFX = 'signalclash.audio.sfx';
const STORAGE_KEY_MUSIC = 'signalclash.audio.music';
const STORAGE_KEY_VOICE = 'signalclash.audio.voice';

import { analytics } from '../analytics/Analytics.js';

const AUDIO_EXTENSIONS: readonly string[] = ['mp3', 'wav'];

const MUSIC_DEFAULT_FADE_MS = 800;

interface MusicHandle {
  name: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

// Voice playback is a separate bus from SFX (master → voice gain → out) so
// the player can dial back chatter without losing combat feedback. Only one
// voice line plays at a time — see `playVoice` / `stopVoice`. Priority gating
// lives one layer up in `Voice.ts`; this class just plays what it's told.
interface VoiceHandle {
  name: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
  priority: number;
}

export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfx: GainNode | null = null;
  private music: GainNode | null = null;
  // Ambience is a third bus that rides under SFX (wind loop, building hum,
  // etc.). It's gated by the same master volume but follows the music slider
  // — ambience feels like score, not action.
  private ambience: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private pending = new Map<string, Promise<AudioBuffer>>();
  private voice: GainNode | null = null;
  private masterVolume = 1;
  private sfxVolume = 1;
  private musicVolume = 0.6;
  private voiceVolume = 0.85;
  private base: string;
  private currentMusic: MusicHandle | null = null;
  private currentAmbience: MusicHandle | null = null;
  private pendingAmbienceName: string | null = null;
  // Voice handle reflects the source currently playing on the voice bus.
  // Cleared in `source.onended` so callers can poll `isVoicePlaying()` to
  // decide whether to drop or interrupt a new line. The Voice layer also
  // reads `getCurrentVoicePriority()` to gate lower-priority cues.
  private currentVoice: VoiceHandle | null = null;
  // Names known to be missing on the voice path. The first failed load logs
  // a single dev-only warning; subsequent loads of the same name short-circuit
  // silently so a 58-file voice pack with a few missing assets doesn't spam
  // the console every time the trigger fires.
  private knownMissingVoice = new Set<string>();
  // Name of the track whose buffer is currently loading. Used as a second
  // idempotency guard alongside `currentMusic`: between `playMusic('X')` being
  // called and the awaited `loadBuffer` resolving, `currentMusic` is still the
  // PREVIOUS track. Without `pendingMusicName`, snapshot ticks 50 ms apart all
  // pass the `currentMusic?.name === name` guard, queue parallel loads, and
  // each spawns its own source — multiple copies of the same track playing
  // on top of each other. With this field, repeat callers bail out
  // immediately while the first load is in flight.
  private pendingMusicName: string | null = null;

  constructor(opts?: { baseUrl?: string }) {
    this.base = opts?.baseUrl ?? './audio';
    try {
      const m = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_MASTER) : null;
      const s = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_SFX) : null;
      const mu = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_MUSIC) : null;
      const v = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_VOICE) : null;
      if (m !== null) this.masterVolume = clamp01(parseFloat(m));
      if (s !== null) this.sfxVolume = clamp01(parseFloat(s));
      if (mu !== null) this.musicVolume = clamp01(parseFloat(mu));
      if (v !== null) this.voiceVolume = clamp01(parseFloat(v));
    } catch {
      // localStorage unavailable; fall back to defaults.
    }
  }

  // Pause/resume without persisting. Used by the PauseController for
  // visibility-driven mutes so we don't overwrite the user's saved
  // volume setting. Suspending the AudioContext also kills timers /
  // CPU usage of currently-playing sources.
  suspendForPause(): void {
    if (this.ctx === null) return;
    if (this.ctx.state === 'running') {
      // .catch to keep a context that's already transitioning out from
      // becoming an unhandled rejection. PauseController fires this on
      // every visibility change so a flaky state would otherwise spam
      // the rejection handler.
      this.ctx.suspend().catch(() => undefined);
    }
  }

  resumeFromPause(): void {
    if (this.ctx === null) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => undefined);
    }
  }

  ensureRunning(): void {
    if (this.ctx === null) {
      // iOS Safari < 14.5 (and the still-shipping Safari builds on older
      // iPhones) only expose the prefixed `webkitAudioContext`. Without
      // the fallback, `new undefined()` throws TypeError on the very
      // first menu click — the exact gesture CrazyGames samples for the
      // Conversion KPI. Treat any non-function as "no audio available"
      // and degrade silently; the game stays playable, just muted.
      const Ctor =
        typeof window !== 'undefined'
          ? (window.AudioContext ??
              (window as Window & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext)
          : undefined;
      if (typeof Ctor !== 'function') {
        // V4.1 — surface the silent-degradation in analytics so the
        // dashboard can see how many sessions had no audio at all
        // (legacy iOS Safari, restrictive WebView). Idempotent at the
        // emitter — the calling code's null-ctx check ensures we only
        // bail-emit per session.
        analytics.emit({
          kind: 'audio_context_failed',
          reason: 'no_audio_context_ctor',
        });
        return;
      }
      try {
        this.ctx = new Ctor();
      } catch (err) {
        analytics.emit({
          kind: 'audio_context_failed',
          reason:
            err instanceof Error ? `${err.name}: ${err.message}` : 'unknown',
        });
        return;
      }
      this.master = this.ctx.createGain();
      this.master.gain.value = this.masterVolume;
      this.sfx = this.ctx.createGain();
      this.sfx.gain.value = this.sfxVolume;
      this.sfx.connect(this.master);
      this.music = this.ctx.createGain();
      this.music.gain.value = this.musicVolume;
      this.music.connect(this.master);
      this.ambience = this.ctx.createGain();
      // Ambience sits well below the music level — it's a bed, not a track.
      // The slider still scales it via the same musicVolume so muting music
      // also kills ambience.
      this.ambience.gain.value = this.musicVolume * 0.5;
      this.ambience.connect(this.master);
      this.voice = this.ctx.createGain();
      this.voice.gain.value = this.voiceVolume;
      this.voice.connect(this.master);
      this.master.connect(this.ctx.destination);
      this.installIosResumeHandler();
    }
    if (this.ctx.state === 'suspended') {
      // .catch — same iOS Safari rationale as suspendForPause.
      this.ctx.resume().catch(() => undefined);
    }
  }

  // iOS Safari can drop the AudioContext into a 'suspended' or
  // 'interrupted' state after backgrounding the tab or after a system
  // event (phone call, Siri). The visibilitychange flow handles tab
  // switches, but interrupted state can persist past that — the only
  // reliable recovery is a user-gesture resume. CrazyGames' submission
  // guide explicitly calls this out for HTML5 games; this listener is
  // the recommended pattern. Installed once after the context exists
  // so it can't fire before there's anything to resume.
  private iosResumeInstalled = false;
  private installIosResumeHandler(): void {
    if (this.iosResumeInstalled) return;
    if (typeof document === 'undefined') return;
    this.iosResumeInstalled = true;
    const resume = () => {
      if (this.ctx !== null && this.ctx.state === 'suspended') {
        // .catch — same iOS Safari rationale as suspendForPause.
        this.ctx.resume().catch(() => undefined);
      }
    };
    document.addEventListener('touchend', resume, { passive: true });
    document.addEventListener('pointerup', resume, { passive: true });
  }

  setMasterVolume(v: number): void {
    this.masterVolume = clamp01(v);
    if (this.master !== null) this.master.gain.value = this.masterVolume;
    persist(STORAGE_KEY_MASTER, this.masterVolume);
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = clamp01(v);
    if (this.sfx !== null) this.sfx.gain.value = this.sfxVolume;
    persist(STORAGE_KEY_SFX, this.sfxVolume);
  }

  setMusicVolume(v: number): void {
    this.musicVolume = clamp01(v);
    if (this.music !== null) this.music.gain.value = this.musicVolume;
    if (this.ambience !== null) this.ambience.gain.value = this.musicVolume * 0.5;
    persist(STORAGE_KEY_MUSIC, this.musicVolume);
  }

  setVoiceVolume(v: number): void {
    this.voiceVolume = clamp01(v);
    if (this.voice !== null) this.voice.gain.value = this.voiceVolume;
    persist(STORAGE_KEY_VOICE, this.voiceVolume);
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getVoiceVolume(): number {
    return this.voiceVolume;
  }

  // Plays a one-shot from the SFX bus. Returns silently if the audio context
  // hasn't started yet (pre-gesture) or the buffer hasn't loaded; sketch SFX
  // are decorative and never block the simulation.
  //
  // `pitchRange` and `volumeJitter` add per-call randomization so repeats of
  // the same cue (rapid weapon fire, machine-gun hit splashes) don't feel
  // mechanically identical. Both default to 0 — UI sounds and event stings
  // stay byte-identical across plays so they keep their signature.
  async playSfx(
    name: string,
    opts?: { volume?: number; pitchRange?: number; volumeJitter?: number },
  ): Promise<void> {
    if (this.ctx === null || this.sfx === null) return;
    const buf = await this.loadBuffer(name);
    if (buf === null || this.ctx === null || this.sfx === null) return;
    // iOS Safari can flip the AudioContext into 'interrupted' or
    // 'closed' state after phone calls / Siri / system events. In
    // those states `createBufferSource` and `start()` throw
    // InvalidStateError. Without this guard a single ill-timed call
    // bubbles back through the snapshot fan-out and kills the match.
    // SFX are decorative — failing silently is the right call.
    try {
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const pitchRange = opts?.pitchRange ?? 0;
      if (pitchRange > 0) {
        src.playbackRate.value = 1 + (Math.random() - 0.5) * pitchRange;
      }
      const baseVol = opts?.volume ?? 1;
      const volJitter = opts?.volumeJitter ?? 0;
      const finalVol = clamp01(baseVol + (Math.random() - 0.5) * volJitter);
      if (finalVol !== 1) {
        const trim = this.ctx.createGain();
        trim.gain.value = finalVol;
        src.connect(trim);
        trim.connect(this.sfx);
      } else {
        src.connect(this.sfx);
      }
      src.start();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[audio] playSfx failed', name, err);
    }
  }

  // Plays a music track on the music bus. Looping by default; passes
  // `{ loop: false }` for one-shot jingles. Cross-fades from whatever music
  // was playing previously, so the caller doesn't need to call `stopMusic`
  // first. Idempotent: re-calling with the same `name` is a no-op so HUD
  // observers can fire `playMusic('music_match_intense')` on every snapshot
  // tick without restarting the track.
  async playMusic(
    name: string,
    opts?: { loop?: boolean; fadeMs?: number; volume?: number },
  ): Promise<void> {
    if (this.ctx === null || this.music === null) return;
    if (this.currentMusic?.name === name) return;
    if (this.pendingMusicName === name) return;

    this.pendingMusicName = name;
    try {
      const buf = await this.loadBuffer(name);
      if (buf === null) return;
      // After the await, bail if another `playMusic`/`stopMusic` call took
      // over with a different target. Without this check we'd start a stale
      // track on top of the new one.
      if (this.ctx === null || this.music === null) return;
      if (this.pendingMusicName !== name) return;
      if (this.currentMusic?.name === name) return;

      const fadeMs = opts?.fadeMs ?? MUSIC_DEFAULT_FADE_MS;
      const targetVolume = clamp01(opts?.volume ?? 1);
      const trackGain = this.ctx.createGain();
      trackGain.gain.value = 0;
      trackGain.connect(this.music);
      const source = this.ctx.createBufferSource();
      source.buffer = buf;
      source.loop = opts?.loop ?? true;
      source.connect(trackGain);
      source.start();
      rampGain(this.ctx, trackGain.gain, targetVolume, fadeMs);

      const previous = this.currentMusic;
      this.currentMusic = { name, source, gain: trackGain };
      if (previous !== null) {
        fadeOutAndStop(this.ctx, previous, fadeMs);
      }
    } catch (err) {
      // iOS Safari throws InvalidStateError if the context flipped to
      // 'interrupted' / 'closed' between the user-gesture resume and
      // this call. Callers fire-and-forget via `void this.bus.playMusic(...)`,
      // so an unhandled rejection here would surface in the global
      // rejection handler installed by main.ts. Music isn't load-bearing —
      // silent recovery is the right call.
      // eslint-disable-next-line no-console
      console.warn('[audio] playMusic failed', name, err);
    } finally {
      if (this.pendingMusicName === name) this.pendingMusicName = null;
    }
  }

  stopMusic(opts?: { fadeMs?: number }): void {
    if (this.ctx === null) return;
    // Clear the pending guard so any in-flight playMusic load bails on resume.
    this.pendingMusicName = null;
    const previous = this.currentMusic;
    if (previous === null) return;
    this.currentMusic = null;
    fadeOutAndStop(this.ctx, previous, opts?.fadeMs ?? MUSIC_DEFAULT_FADE_MS);
  }

  // Ambience runs in parallel with the music bus on its own gain — a wind
  // loop or distant battlefield rumble that sits underneath the bed track
  // without competing with the SFX foreground. Same race-safe shape as
  // `playMusic` (pending-name guard + post-await recheck).
  async playAmbience(
    name: string,
    opts?: { fadeMs?: number; volume?: number },
  ): Promise<void> {
    if (this.ctx === null || this.ambience === null) return;
    if (this.currentAmbience?.name === name) return;
    if (this.pendingAmbienceName === name) return;

    this.pendingAmbienceName = name;
    try {
      const buf = await this.loadBuffer(name);
      if (buf === null) return;
      if (this.ctx === null || this.ambience === null) return;
      if (this.pendingAmbienceName !== name) return;
      if (this.currentAmbience?.name === name) return;

      const fadeMs = opts?.fadeMs ?? MUSIC_DEFAULT_FADE_MS;
      const targetVolume = clamp01(opts?.volume ?? 1);
      const trackGain = this.ctx.createGain();
      trackGain.gain.value = 0;
      trackGain.connect(this.ambience);
      const source = this.ctx.createBufferSource();
      source.buffer = buf;
      source.loop = true;
      source.connect(trackGain);
      source.start();
      rampGain(this.ctx, trackGain.gain, targetVolume, fadeMs);

      const previous = this.currentAmbience;
      this.currentAmbience = { name, source, gain: trackGain };
      if (previous !== null) fadeOutAndStop(this.ctx, previous, fadeMs);
    } catch (err) {
      // Same iOS-Safari context-state rationale as playMusic.
      // eslint-disable-next-line no-console
      console.warn('[audio] playAmbience failed', name, err);
    } finally {
      if (this.pendingAmbienceName === name) this.pendingAmbienceName = null;
    }
  }

  stopAmbience(opts?: { fadeMs?: number }): void {
    if (this.ctx === null) return;
    this.pendingAmbienceName = null;
    const previous = this.currentAmbience;
    if (previous === null) return;
    this.currentAmbience = null;
    fadeOutAndStop(this.ctx, previous, opts?.fadeMs ?? MUSIC_DEFAULT_FADE_MS);
  }

  // Plays a voice line on the voice bus. Priority gating + cooldowns live in
  // `Voice.ts`; this method assumes the caller has already decided the line
  // should play. If a previous voice is still on the bus it is stopped first
  // so we never overlap two lines (Voice queueing isn't appropriate for an
  // RTS — a stale "Recon online" landing over "Core integrity critical" reads
  // worse than a dropped line). `priority` is recorded so the caller can read
  // it back via `getCurrentVoicePriority()` before deciding to interrupt.
  //
  // Missing voice files fail silently: the first attempt logs one dev-only
  // hint, the name is remembered, and subsequent triggers short-circuit so
  // the console stays readable during gameplay.
  async playVoice(
    name: string,
    opts?: { priority?: number; volume?: number },
  ): Promise<boolean> {
    if (this.ctx === null || this.voice === null) return false;
    if (this.knownMissingVoice.has(name)) return false;
    const buf = await this.loadBuffer(name);
    if (buf === null) {
      if (!this.knownMissingVoice.has(name)) {
        this.knownMissingVoice.add(name);
        // eslint-disable-next-line no-console
        if (typeof console !== 'undefined') console.debug(`[voice] missing asset: ${name}`);
      }
      return false;
    }
    if (this.ctx === null || this.voice === null) return false;
    // Stop any in-flight voice. Priority decision lives in Voice.ts — if we
    // got here we already decided this line should land.
    this.stopVoice();
    // iOS Safari can throw InvalidStateError on createBufferSource /
    // start() when the context flipped to 'interrupted' or 'closed'
    // between the user gesture and this call. Voice lines are
    // decorative — log and degrade.
    try {
      const trim = this.ctx.createGain();
      trim.gain.value = clamp01(opts?.volume ?? 1);
      trim.connect(this.voice);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(trim);
      const handle: VoiceHandle = { name, source: src, gain: trim, priority: opts?.priority ?? 1 };
      src.onended = (): void => {
        if (this.currentVoice === handle) this.currentVoice = null;
      };
      this.currentVoice = handle;
      src.start();
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[audio] playVoice failed', name, err);
      return false;
    }
  }

  stopVoice(): void {
    const h = this.currentVoice;
    if (h === null) return;
    this.currentVoice = null;
    try {
      h.source.onended = null;
      h.source.stop();
    } catch {
      // Already stopped — ignore.
    }
  }

  isVoicePlaying(): boolean {
    return this.currentVoice !== null;
  }

  getCurrentVoicePriority(): number | null {
    return this.currentVoice?.priority ?? null;
  }

  getCurrentVoiceName(): string | null {
    return this.currentVoice?.name ?? null;
  }

  // What track is currently playing (or null if none). Lets the MusicDirector
  // make idempotent decisions without re-issuing playMusic for the same name.
  getCurrentMusicName(): string | null {
    return this.currentMusic?.name ?? null;
  }

  private async loadBuffer(name: string): Promise<AudioBuffer | null> {
    if (this.ctx === null) return null;
    const cached = this.buffers.get(name);
    if (cached !== undefined) return cached;
    const inflight = this.pending.get(name);
    if (inflight !== undefined) return inflight;
    const ctx = this.ctx;
    const base = this.base;
    const promise = (async (): Promise<AudioBuffer> => {
      let lastErr: unknown = null;
      for (const ext of AUDIO_EXTENSIONS) {
        const url = `${base}/${name}.${ext}`;
        try {
          const res = await fetch(url);
          if (!res.ok) {
            lastErr = new Error(`HTTP ${res.status} for ${url}`);
            continue;
          }
          const ab = await res.arrayBuffer();
          const decoded = await ctx.decodeAudioData(ab);
          this.buffers.set(name, decoded);
          this.pending.delete(name);
          return decoded;
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr ?? new Error(`no audio file found for ${name}`);
    })();
    this.pending.set(name, promise);
    try {
      return await promise;
    } catch {
      this.pending.delete(name);
      return null;
    }
  }
}

function rampGain(ctx: AudioContext, param: AudioParam, target: number, fadeMs: number): void {
  const now = ctx.currentTime;
  const end = now + Math.max(0.001, fadeMs / 1000);
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(target, end);
}

function fadeOutAndStop(ctx: AudioContext, handle: MusicHandle, fadeMs: number): void {
  rampGain(ctx, handle.gain.gain, 0, fadeMs);
  // Stop a touch after the fade ends so the linearRamp finishes.
  const stopAt = ctx.currentTime + Math.max(0.001, fadeMs / 1000) + 0.05;
  try {
    handle.source.stop(stopAt);
  } catch {
    // Already stopped; ignore.
  }
}

function persist(key: string, value: number): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, String(value));
    }
  } catch {
    // no-op
  }
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
